import React from 'react';
import { View, Font, Svg, Circle, Polyline, G, Path, Text, Line } from '@react-pdf/renderer';
import { useGauge } from "use-gauge";

Font.register({
  family: 'Inter',
  fonts: [
    { src: `${process.env.PUBLIC_URL}/font/Inter-Regular.ttf` },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Medium.ttf`, fontWeight: 500 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-SemiBold.ttf`, fontWeight: 600 },
    { src: `${process.env.PUBLIC_URL}/font/Inter-Bold.ttf`, fontWeight: 700 }
  ]
});

Font.registerHyphenationCallback(word => [word]);

interface ArcedProps {
  value: number;
  roundedM: number;
  roundedS: number;
  color: string;
}

const START_ANGLE = 45;
const END_ANGLE = 315;

const constrainValue = (value: number, min: number, max: number) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const valueToAngle = (value: number) => {
  const scale = (END_ANGLE - START_ANGLE) / 4;
  return (value - 1) * scale + START_ANGLE;
};

export function Arced(props: ArcedProps) {
  const { value, roundedM, roundedS, color } = props;
  const constrainedValue = constrainValue(value, 1, 5);

  const firstPoint = Math.round(valueToAngle(roundedM - roundedS));
  const secondPoint = Math.round(valueToAngle(roundedM));
  const thirdPoint = Math.round(valueToAngle(roundedM + roundedS));
  console.log(roundedM, roundedS);

  const isZeroState = roundedM === 0 && roundedS === 0;

  const gauge = useGauge({
    domain: [1, 5],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: 5,
    diameter: 300
  });

  const needle = gauge.getNeedleProps({
    value: constrainedValue,
    baseRadius: 14,
    tipRadius: 1
  });

  return (
    <View style={{ padding: 0 }}>
      <Svg
        style={{
          width: '100%',
          overflow: 'visible',
          padding: 0,
        }}
        {...gauge.getSVGProps()}
      >
        <G id="arcs" style={{ transform: 'scale(0.7, 0.7)' }}>
          <Path id="base"
            {...gauge.getArcProps({
              offset: 30,
              startAngle: START_ANGLE,
              endAngle: END_ANGLE
            })}
            fill="none"
            style={{ stroke: isZeroState ? '#e03c32' : '#639754' }}
            strokeLinecap="round"
            strokeWidth={30}
          />
          {!isZeroState && (
            <>
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: START_ANGLE,
                  endAngle: firstPoint
                })}
                fill="none"
                style={{ stroke: '#e03c32' }}
                strokeLinecap="round"
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: firstPoint,
                  endAngle: secondPoint
                })}
                fill="none"
                style={{ stroke: '#ffd301' }}
                strokeWidth={30}
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: secondPoint - 3,
                  endAngle: thirdPoint - 3
                })}
                fill="none"
                style={{ stroke: '#FFFFFF' }}
                strokeWidth={34} 
              />
              <Path
                {...gauge.getArcProps({
                  offset: 30,
                  startAngle: secondPoint,
                  endAngle: thirdPoint
                })}
                fill="none"
                style={{ stroke: '#7bb662' }}
                strokeWidth={30}
              />
            </>
          )}
        </G>
        <G id="ticks" style={{ transform: 'scale(0.7, 0.7)' }}>
          {gauge.ticks.map((angle) => {
            const asValue = gauge.angleToValue(angle);
            const showText = asValue === 1 || asValue === 2 || asValue === 3 || asValue === 4 || asValue === 5;
            return (
              <React.Fragment key={`tick-group-${angle}`}>
                <Line
                  strokeWidth={1}
                  stroke={color}
                  strokeOpacity={0.7}
                  {...gauge.getTickProps({ angle, length: showText ? 12 : 6 })}
                />
                {showText && (
                  <Text
                    style={{ fontSize: 24 }}
                    {...gauge.getLabelProps({ angle, offset: -70 })}
                    fill={color}
                  >
                    {asValue}
                  </Text>
                )}
              </React.Fragment>
            );
          })}
        </G>
        <G id="needle" style={{ transform: 'scale(0.7, 0.7)' }}>
          <Circle fill={color} {...needle.base} />
          <Circle fill={color} {...needle.tip} />
          <Polyline fill={color} points={needle.points} />
        </G>
      </Svg>
    </View>
  );
}
